import { useState, useEffect } from 'react';
import axios from 'axios';
import { LARAVEL_API_PATH } from '../constants/path';
import { REQUIRED_CONSENT_TYPES } from '../helpers';
import { 
    AvailableConsentTypesResponse, 
    ConsentTypeInfo,
    ConsentEventsSearchResponse
} from '../components/ConsentModal';
import { useTypedSelector } from './useTypedSelector';

interface UseConsentCheckReturn {
    showModal: boolean;
    loading: boolean;
    consentTypesInfo: AvailableConsentTypesResponse | null;
    handleModalClose: () => void;
}

/**
 * Хук для проверки необходимости показа модального окна согласий
 * Проверяет версии документов на сервере и сравнивает с подписанными согласиями пользователя из API
 */
export const useConsentCheck = (): UseConsentCheckReturn => {
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [consentTypesInfo, setConsentTypesInfo] = useState<AvailableConsentTypesResponse | null>(null);
    const { patient } = useTypedSelector(state => state.patientState);

    useEffect(() => {
        const checkConsentVersions = async () => {
            // Проверяем наличие токена - если его нет, это страница логина
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            // Проверяем наличие ID пациента
            if (!patient?.id) {
                setLoading(false);
                return;
            }

            try {
                // Получаем доступные типы согласий с сервера
                const availableResponse = await axios.get<AvailableConsentTypesResponse>(
                    `${LARAVEL_API_PATH}consent-download/types/available`
                );

                if (!availableResponse.data || !availableResponse.data.types_info) {
                    setLoading(false);
                    return;
                }

                setConsentTypesInfo(availableResponse.data);

                // Получаем подписанные согласия пользователя
                const searchResponse = await axios.get<ConsentEventsSearchResponse>(
                    `${LARAVEL_API_PATH}consent-download/events/search`,
                    {
                        params: {
                            lk_id: patient.id
                        }
                    }
                );

                // Создаем карту подписанных согласий: consent_type -> максимальная text_version
                const signedConsents = new Map<string, number>();
                if (searchResponse.data?.rows) {
                    searchResponse.data.rows.forEach((row) => {
                        // Берем только согласия с action='granted' (подписанные)
                        // Но в ответе /search нет поля action, поэтому берем все записи как подписанные
                        // Преобразуем text_version в число, так как API может возвращать строку
                        const textVersion = typeof row.text_version === 'string' 
                            ? parseInt(row.text_version, 10) 
                            : row.text_version;
                        const currentVersion = signedConsents.get(row.consent_type) || 0;
                        if (textVersion > currentVersion) {
                            signedConsents.set(row.consent_type, textVersion);
                        }
                    });
                }

                // Проверяем, нужно ли показывать модальное окно
                // Сравниваем версии из /types/available с версиями из /search
                let needsConsent = false;
                
                for (const consentType of REQUIRED_CONSENT_TYPES) {
                    const typeInfo: ConsentTypeInfo | undefined = availableResponse.data.types_info[consentType];
                    if (!typeInfo) {
                        continue;
                    }

                    const serverVersion = typeInfo.text_version;
                    const signedVersion = signedConsents.get(consentType);

                    // Если нет подписанного согласия или версия на сервере выше подписанной
                    if (!signedVersion || serverVersion > signedVersion) {
                        needsConsent = true;
                        break;
                    }
                }

                setShowModal(needsConsent);

            } catch (error) {
                console.error('Error checking consent versions:', error);
                // В случае ошибки не показываем модальное окно
                setShowModal(false);
            } finally {
                setLoading(false);
            }
        };

        checkConsentVersions();
    }, [patient?.id]);

    const handleModalClose = () => {
        setShowModal(false);
    };

    return {
        showModal,
        loading,
        consentTypesInfo,
        handleModalClose
    };
};

